import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Package, Search, X, PackageMinus, Download, AlertTriangle } from "lucide-react";
import { toast } from "react-toastify";

import "../styles/scansheet.css";
import "../styles/stock-check.css";
import "../styles/write-off.css";
import { useTranslation } from "../i18n";
import { usePageTitle } from "../hooks/usePageTitle";
import { logoutProcessor } from "../components/helpers/logoutProcessor";
import { apiFetch } from "../api/apiFetch";
import { useReadOnly } from "../access/ReadOnly";
import { useWarehouseScope } from "../access/useWarehouseScope";
import {
    WRITE_OFF_REASONS,
    reasonKey,
    lineKey,
    normalizeLines,
    normalizeProduct,
    normalizeSkuLines,
    initialSelection,
    parseQty,
    lineProblems,
    isBlocking,
    validateSelection,
    formatMoney,
    historyQuery,
    formatWhen,
    EMPTY_HISTORY_FILTERS,
    STOCK_FLAGS,
    flagKey,
    filterLines,
    flagCounts,
    EMPTY_DATE_RANGE,
    dateRangeActive,
    filterByPutHere,
    unitsPutHereBetween,
    putHereLabel,
    putHereBreakdown,
} from "./write-off-helpers";
import type {
    StockFlag,
    DateRange,
    WriteOffLine,
    WriteOffLocation,
    WriteOffProduct,
    SearchMode,
    Selection,
    SelectionResult,
    HistoryFilters,
    HistoryRow,
    LineProblem,
} from "./write-off-helpers";

// Stock Control → Write Off (admin-only; not an operative screen).
//
// Two ways in, one table:
//   By location — search a location by barcode or display name and see
//     every SKU sitting there (one row per SKU).
//   By SKU — search a SKU or product barcode and see every location in the
//     warehouse holding it (one row per location). Alex, 2026-10-19.
// Either way: tick the rows to remove, set a quantity per row (default =
// everything not allocated), choose a reason, confirm. Allocated units can
// never be written off; reserved stock is SKU-wide, so "sold offline" is
// capped at free stock — per row by location, on the TOTAL by SKU — while
// the other reasons warn and proceed. The History tab lists every
// write-off with filters and an XLSX export served by the BE.

type Tab = "write_off" | "history";
const HISTORY_PAGE_SIZE = 50;

// Rendered only inside Admin › Warehouse (admin-write-off.tsx supplies the
// header + breadcrumbs). Deliberately NOT on the app's Stock Control hub —
// Alex, 2026-09-15: write-offs are an admin task, never a handheld one.
const WriteOff = () => {
    usePageTitle("write_off");
    const navigate = useNavigate();
    const { t } = useTranslation();
    const readOnly = useReadOnly();

    const userData = window.localStorage.getItem("user");
    const userObject =
        userData !== null ? JSON.parse(userData) : { uid: 0, name: "", permissions: "", warehouse: "Unknown" };
    // Warehouse (Alex, 2026-10-26): starts as the admin section's warehouse
    // (localStorage, pivoted by AdminWarehouseSwitcher) and, for a caller
    // holding cross_warehouse, can be changed right here without a reload.
    // Every request carries it; the BE guard already honours it (D12).
    // Names come from GET /settings, the reference read every warehouse
    // picker uses (hidden warehouses are left out there).
    const scope = useWarehouseScope();
    const [warehouse, setWarehouse] = useState<string>(userObject.warehouse ?? "");
    const [warehouseNames, setWarehouseNames] = useState<string[]>([]);
    useEffect(() => {
        if (!scope.canCross) return;
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(`/settings`, {});
                if (!res.ok) return;
                const data = await res.json().catch(() => null);
                if (!alive || !Array.isArray(data)) return;
                setWarehouseNames(
                    data.map((r: any) => (typeof r?.warehouse === "string" ? r.warehouse : "")).filter(Boolean).sort(),
                );
            } catch { /* keep the current warehouse only */ }
        })();
        return () => { alive = false; };
    }, [scope.canCross]);
    // What the dropdown offers: the live list narrowed by scope, always
    // including whatever is current so the control never shows a blank.
    const warehouseOptions = scope.limit(
        warehouse && !warehouseNames.includes(warehouse) ? [warehouse, ...warehouseNames] : warehouseNames,
    );
    const showWarehousePicker = scope.canCross && warehouseOptions.length > 1;

    const [tab, setTab] = useState<Tab>("write_off");

    // ---- Write-off tab state ----
    const [mode, setMode] = useState<SearchMode>("location");
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [location, setLocation] = useState<WriteOffLocation | null>(null);
    const [product, setProduct] = useState<WriteOffProduct | null>(null);
    // By SKU: other warehouses holding the SKU, so an empty list can say where it is.
    const [elsewhere, setElsewhere] = useState<{ warehouse: string; on_hand: number }[]>([]);
    const [lines, setLines] = useState<WriteOffLine[]>([]);
    // Row filter chips (on hand / allocated / reserved / unsellable); empty = show all.
    const [flags, setFlags] = useState<StockFlag[]>([]);
    // Put-here date range (Alex, 2026-11-01): rows with units put at the location inside it.
    const [putHere, setPutHere] = useState<DateRange>(EMPTY_DATE_RANGE);
    const [selection, setSelection] = useState<Selection>({});
    const [reason, setReason] = useState<string>("");
    const [reference, setReference] = useState("");
    const [confirm, setConfirm] = useState<SelectionResult | null>(null);
    const [submitting, setSubmitting] = useState(false);

    // ---- History tab state ----
    const [filters, setFilters] = useState<HistoryFilters>(EMPTY_HISTORY_FILTERS);
    const [applied, setApplied] = useState<HistoryFilters>(EMPTY_HISTORY_FILTERS);
    const [historyRows, setHistoryRows] = useState<HistoryRow[]>([]);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [historyPage, setHistoryPage] = useState(0);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [exporting, setExporting] = useState(false);

    const mountedRef = useRef(true);
    useEffect(() => () => { mountedRef.current = false; }, []);

    const bounce = useCallback((res: Response) => {
        if (res.status === 401) {
            logoutProcessor();
            navigate("/login");
            return true;
        }
        return false;
    }, [navigate]);

    // ---- Location lookup ----------------------------------------------

    const loadLocation = async (q: string) => {
        const needle = q.trim();
        if (!needle) return;
        setLoading(true);
        try {
            const params = new URLSearchParams({ q: needle, warehouse });
            const res = await apiFetch(`/write-off/location?${params.toString()}`, {});
            if (bounce(res)) return;
            const data = await res.json().catch(() => null);
            if (!res.ok || !data) {
                toast.error(t("server_error"), { toastId: "wo-server" });
                return;
            }
            if (!data.found) {
                setLocation(null);
                setLines([]);
                setSelection({});
                toast.error(t("write_off_unknown_location"), { toastId: "wo-loc" });
                return;
            }
            const next = normalizeLines(data.lines);
            setLocation(data.location);
            setLines(next);
            setSelection(initialSelection(next));
        } catch {
            if (mountedRef.current) toast.error(t("server_error"), { toastId: "wo-server" });
        } finally {
            if (mountedRef.current) setLoading(false);
        }
    };

    // ---- SKU lookup ---------------------------------------------------

    const loadSku = async (q: string) => {
        const needle = q.trim();
        if (!needle) return;
        setLoading(true);
        try {
            const params = new URLSearchParams({ q: needle, warehouse });
            const res = await apiFetch(`/write-off/sku?${params.toString()}`, {});
            if (bounce(res)) return;
            const data = await res.json().catch(() => null);
            if (!res.ok || !data) {
                toast.error(t("server_error"), { toastId: "wo-server" });
                return;
            }
            if (!data.found) {
                setProduct(null);
                setLines([]);
                setSelection({});
                toast.error(t("write_off_unknown_sku"), { toastId: "wo-sku" });
                return;
            }
            const next = normalizeProduct(data.product);
            const nextLines = normalizeSkuLines(data.lines, next);
            setProduct(next);
            setElsewhere(
                (Array.isArray(data.elsewhere) ? data.elsewhere : [])
                    .map((e: any) => ({ warehouse: String(e?.warehouse ?? ""), on_hand: Number(e?.on_hand ?? 0) }))
                    .filter((e: { warehouse: string; on_hand: number }) => e.warehouse && e.on_hand > 0),
            );
            setLines(nextLines);
            setSelection(initialSelection(nextLines));
        } catch {
            if (mountedRef.current) toast.error(t("server_error"), { toastId: "wo-server" });
        } finally {
            if (mountedRef.current) setLoading(false);
        }
    };

    const onSearch = (e?: React.FormEvent) => {
        e?.preventDefault();
        if (mode === "location") void loadLocation(query);
        else void loadSku(query);
    };

    // Switching the way in drops the current result — the table's rows mean
    // something different in each mode, so nothing carries over.
    const switchMode = (next: SearchMode) => {
        if (next === mode) return;
        setMode(next);
        setQuery("");
        setLocation(null);
        setProduct(null);
        setElsewhere([]);
        setLines([]);
        setFlags([]);
        setPutHere(EMPTY_DATE_RANGE);
        setSelection({});
    };

    // Changing warehouse drops the result the same way — the rows belong to
    // the old site — but keeps the mode and the typed query so the same
    // search can be re-run against the new one. History re-reads itself
    // (loadHistory depends on `warehouse`) from page 0.
    const changeWarehouse = (next: string) => {
        if (!next || next === warehouse) return;
        setWarehouse(next);
        setLocation(null);
        setProduct(null);
        setElsewhere([]);
        setLines([]);
        setFlags([]);
        setPutHere(EMPTY_DATE_RANGE);
        setSelection({});
        setHistoryPage(0);
    };

    // Re-read the figures for whatever is on screen.
    const reload = async () => {
        if (mode === "location") { if (location) await loadLocation(location.barcode); }
        else if (product) await loadSku(product.sku);
    };

    const hasResult = mode === "location" ? location !== null : product !== null;

    // ---- Selection ----------------------------------------------------

    // ---- Row filter ---------------------------------------------------
    // By location the shelf's sellable flag applies to every row; by SKU
    // each row carries its own. The filter only changes what is SHOWN —
    // a row ticked before being filtered out stays ticked and still
    // counts in the footer and the confirm, so nothing is lost by
    // flipping chips. Select-all works on the visible rows only.
    const flagCtx = mode === "location" && location ? { locationSellable: location.sellable } : {};
    const counts = flagCounts(lines, flagCtx);
    // Chips first, then the put-here range — both only narrow what is shown.
    const visibleLines = filterByPutHere(filterLines(lines, flags, flagCtx), putHere);
    const toggleFlag = (flag: StockFlag) =>
        setFlags((f) => (f.includes(flag) ? f.filter((x) => x !== flag) : [...f, flag]));
    const putHereOn = dateRangeActive(putHere);
    const putHereUnits = putHereOn ? visibleLines.reduce((a, l) => a + unitsPutHereBetween(l, putHere), 0) : 0;
    const filtersOn = flags.length > 0 || putHereOn;
    const clearRowFilters = () => { setFlags([]); setPutHere(EMPTY_DATE_RANGE); };

    const toggle = (key: string, checked: boolean) =>
        setSelection((s) => ({ ...s, [key]: { ...(s[key] ?? { qty: "" }), checked } }));
    const setQty = (key: string, qty: string) =>
        setSelection((s) => ({ ...s, [key]: { ...(s[key] ?? { checked: false }), qty } }));
    const allChecked = visibleLines.length > 0 && visibleLines.every((l) => selection[lineKey(l)]?.checked);
    const toggleAll = (checked: boolean) =>
        setSelection((s) => {
            const out: Selection = { ...s };
            for (const l of visibleLines) {
                const k = lineKey(l);
                out[k] = { ...(s[k] ?? { qty: String(l.writable_qty) }), checked };
            }
            return out;
        });

    // By SKU the free stock is one SKU-wide number shared by every row, so
    // the total is what is judged against it (see validateSelection).
    const validateOptions = mode === "sku" && product ? { sharedFreeStock: product.free_stock } : {};
    const live = validateSelection(lines, selection, reason || null, validateOptions);

    const problemText = (p: LineProblem, line: WriteOffLine) => {
        if (p === "invalid_qty") return t("write_off_problem_invalid_qty");
        if (p === "over_writable") return t("write_off_problem_over_writable", { max: String(line.writable_qty) });
        return t("write_off_problem_over_free_stock", { free: String(line.free_stock) });
    };

    const errorText = (error: SelectionResult["error"], sku?: string) => {
        switch (error) {
            case "no_lines":        return t("write_off_error_no_lines");
            case "no_reason":       return t("write_off_error_no_reason");
            case "invalid_qty":     return t("write_off_error_invalid_qty", { sku: sku ?? "" });
            case "over_writable":   return t("write_off_error_over_writable", { sku: sku ?? "" });
            case "over_free_stock":
                return mode === "sku"
                    ? t("write_off_error_total_over_free_stock", { free: String(product?.free_stock ?? 0) })
                    : t("write_off_error_over_free_stock", { sku: sku ?? "" });
            default:                return t("server_error");
        }
    };

    const openConfirm = () => {
        const result = validateSelection(lines, selection, reason || null, validateOptions);
        if (!result.ok) {
            toast.error(errorText(result.error, result.errorSku), { toastId: "wo-validate" });
            return;
        }
        setConfirm(result);
    };

    // ---- Submit -------------------------------------------------------

    // The by-SKU refusals name the location (location_name); the by-location
    // ones name the SKU. Same codes, different wording.
    const beReasonText = (data: any): string => {
        const loc: string | undefined = data?.location_name ?? undefined;
        const sku = data?.sku ?? "";
        switch (data?.reason) {
            case "unknown_location":  return t("write_off_unknown_location");
            case "unknown_sku":       return t("write_off_unknown_sku");
            case "invalid_reason":    return t("write_off_error_no_reason");
            case "no_lines":          return t("write_off_error_no_lines");
            case "invalid_line":      return t("write_off_error_invalid_qty", { sku });
            case "not_at_location":
                return loc ? t("write_off_be_not_at_location_sku", { sku, location: loc }) : t("write_off_be_not_at_location", { sku });
            case "exceeds_writable":
                return loc
                    ? t("write_off_be_exceeds_writable_sku", { location: loc, writable: String(data.writable ?? 0) })
                    : t("write_off_be_exceeds_writable", { sku, writable: String(data.writable ?? 0) });
            case "exceeds_free_stock":return t("write_off_be_exceeds_free_stock", { sku, free: String(data.free ?? 0) });
            case "stock_moved":
                return loc ? t("write_off_be_stock_moved_sku", { location: loc }) : t("write_off_be_stock_moved", { sku });
            default:                  return t("server_error");
        }
    };

    const submit = async () => {
        if (!confirm || !hasResult) return;
        const target = mode === "location" && location
            ? { location: location.barcode, lines: confirm.lines.map((l) => ({ sku: l.sku, quantity: l.quantity })) }
            : product
                ? { sku: product.sku, lines: confirm.lines.map((l) => ({ location: l.key, quantity: l.quantity })) }
                : null;
        if (!target) return;
        setSubmitting(true);
        try {
            const res = await apiFetch(`/write-off`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    warehouse,
                    uid: userObject.uid,
                    name: userObject.name,
                    ...target,
                    reason,
                    reference: reference.trim() || null,
                }),
            });
            if (bounce(res)) return;
            const data = await res.json().catch(() => null);
            if (!res.ok || !data?.success) {
                toast.error(beReasonText(data), { toastId: "wo-submit" });
                // The shelf may have changed under us — refresh the figures.
                if (data?.reason === "stock_moved" || data?.reason === "exceeds_writable") await reload();
                return;
            }
            toast.success(
                t("write_off_done", { units: String(data.total_units ?? 0), value: formatMoney(Number(data.total_cost ?? 0)) }),
                { toastId: "wo-done" },
            );
            setReference("");
            await reload();
        } catch {
            if (mountedRef.current) toast.error(t("server_error"), { toastId: "wo-server" });
        } finally {
            if (mountedRef.current) { setSubmitting(false); setConfirm(null); }
        }
    };

    // ---- History ------------------------------------------------------

    const loadHistory = useCallback(async (f: HistoryFilters, page: number) => {
        setHistoryLoading(true);
        try {
            const qs = historyQuery(warehouse, f, { page, pageSize: HISTORY_PAGE_SIZE });
            const res = await apiFetch(`/write-off/history?${qs}`, {});
            if (bounce(res)) return;
            const data = await res.json().catch(() => null);
            if (!res.ok || !data) {
                toast.error(t("server_error"), { toastId: "wo-server" });
                return;
            }
            if (!mountedRef.current) return;
            setHistoryRows(Array.isArray(data.rows) ? data.rows : []);
            setHistoryTotal(Number(data.total ?? 0));
        } catch {
            if (mountedRef.current) toast.error(t("server_error"), { toastId: "wo-server" });
        } finally {
            if (mountedRef.current) setHistoryLoading(false);
        }
    }, [warehouse, bounce, t]);

    useEffect(() => {
        if (tab === "history") void loadHistory(applied, historyPage);
    }, [tab, applied, historyPage, loadHistory]);

    const applyFilters = (e?: React.FormEvent) => {
        e?.preventDefault();
        setHistoryPage(0);
        setApplied(filters);
    };
    const clearFilters = () => {
        setFilters(EMPTY_HISTORY_FILTERS);
        setHistoryPage(0);
        setApplied(EMPTY_HISTORY_FILTERS);
    };

    const exportHistory = async () => {
        setExporting(true);
        try {
            const res = await apiFetch(`/write-off/history/export?${historyQuery(warehouse, applied)}`, {});
            if (bounce(res)) return;
            if (!res.ok) { toast.error(t("server_error"), { toastId: "wo-server" }); return; }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `write-offs-${warehouse}-${new Date().toISOString().slice(0, 10)}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch {
            if (mountedRef.current) toast.error(t("server_error"), { toastId: "wo-server" });
        } finally {
            if (mountedRef.current) setExporting(false);
        }
    };

    const pageStart = historyTotal === 0 ? 0 : historyPage * HISTORY_PAGE_SIZE + 1;
    const pageEnd = Math.min(historyTotal, (historyPage + 1) * HISTORY_PAGE_SIZE);

    // ---- Render -------------------------------------------------------

    return (
        <div className="write-off-panel write-off-panel--admin">

            <div className="write-off-tabs" role="tablist">
                <button
                    role="tab"
                    aria-selected={tab === "write_off"}
                    className={`write-off-tab${tab === "write_off" ? " write-off-tab--active" : ""}`}
                    onClick={() => setTab("write_off")}
                >
                    {t("write_off")}
                </button>
                <button
                    role="tab"
                    aria-selected={tab === "history"}
                    className={`write-off-tab${tab === "history" ? " write-off-tab--active" : ""}`}
                    onClick={() => setTab("history")}
                >
                    {t("write_off_history")}
                </button>
            </div>

            {tab === "write_off" && (
                <div className="write-off-body">
                    <form className="write-off-search" onSubmit={onSearch}>
                        {showWarehousePicker && (
                            <select
                                className="write-off-warehouse"
                                aria-label={t("write_off_warehouse")}
                                value={warehouse}
                                onChange={(e) => changeWarehouse(e.target.value)}
                            >
                                {warehouseOptions.map((w) => <option key={w} value={w}>{w}</option>)}
                            </select>
                        )}
                        <div className="write-off-mode" role="radiogroup" aria-label={t("write_off_mode")}>
                            <button
                                type="button"
                                role="radio"
                                aria-checked={mode === "location"}
                                className={`write-off-mode-btn${mode === "location" ? " write-off-mode-btn--active" : ""}`}
                                onClick={() => switchMode("location")}
                            >
                                <MapPin size={14} />
                                {t("write_off_mode_location")}
                            </button>
                            <button
                                type="button"
                                role="radio"
                                aria-checked={mode === "sku"}
                                className={`write-off-mode-btn${mode === "sku" ? " write-off-mode-btn--active" : ""}`}
                                onClick={() => switchMode("sku")}
                            >
                                <Package size={14} />
                                {t("write_off_mode_sku")}
                            </button>
                        </div>
                        <input
                            aria-label={mode === "location" ? t("write_off_search_label") : t("write_off_search_sku_label")}
                            placeholder={mode === "location" ? t("write_off_search_placeholder") : t("write_off_search_sku_placeholder")}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            autoFocus
                        />
                        <button type="submit" className="btn btn-primary" disabled={loading || !query.trim()}>
                            <Search size={18} />
                            {loading ? t("loading") : t("search")}
                        </button>
                    </form>

                    {hasResult && (
                        <>
                            {mode === "location" && location && (
                                <div className="stock-check-header">
                                    <div className="stock-check-loc">
                                        <MapPin size={18} />
                                        <strong>{location.display_name}</strong>
                                        <span className={`write-off-sellable${location.sellable === "unsellable" ? " write-off-sellable--unsellable" : ""}`}>
                                            {location.sellable === "unsellable" ? t("write_off_unsellable") : t("write_off_sellable")}
                                        </span>
                                    </div>
                                    <div className="stock-check-counter">
                                        <span className="stock-check-counter-num">{lines.reduce((a, l) => a + l.units_here, 0)}</span>
                                        <span className="stock-check-counter-den">&nbsp;{t("write_off_units")}</span>
                                    </div>
                                </div>
                            )}

                            {mode === "sku" && product && (
                                <div className="stock-check-header">
                                    <div className="stock-check-loc write-off-product-head">
                                        <Package size={18} />
                                        <div className="write-off-product">
                                            <strong className="write-off-product-title" title={product.title}>{product.title}</strong>
                                            <span className="write-off-product-sku">{product.sku}{product.barcode ? ` · ${product.barcode}` : ""}</span>
                                        </div>
                                        <span className="write-off-free-badge" title={t("write_off_free_stock_hint")}>
                                            {t("write_off_free_stock")}: <strong>{product.free_stock}</strong>
                                        </span>
                                    </div>
                                    <div className="stock-check-counter write-off-counter--sku">
                                        <span className="stock-check-counter-num">{lines.reduce((a, l) => a + l.units_here, 0)}</span>
                                        <span className="stock-check-counter-den">&nbsp;{t("write_off_units")}</span>
                                        <span className="write-off-across">{t("write_off_across_locations", { count: String(lines.length) })}</span>
                                    </div>
                                </div>
                            )}

                            {lines.length > 0 && (
                                <div className="write-off-flags" role="group" aria-label={t("write_off_filter")}>
                                    <span className="write-off-flags-label">{t("write_off_filter")}</span>
                                    {STOCK_FLAGS.map((flag) => {
                                        const on = flags.includes(flag);
                                        return (
                                            <button
                                                key={flag}
                                                type="button"
                                                aria-pressed={on}
                                                className={`write-off-flag${on ? " write-off-flag--on" : ""}${counts[flag] === 0 ? " write-off-flag--none" : ""}`}
                                                title={t(`${flagKey(flag)}_hint`)}
                                                onClick={() => toggleFlag(flag)}
                                            >
                                                {t(flagKey(flag))}
                                                <span className="write-off-flag-count">{counts[flag]}</span>
                                            </button>
                                        );
                                    })}
                                    <span className="write-off-dates" role="group" aria-label={t("write_off_put_here")} title={t("write_off_put_here_hint")}>
                                        <span className="write-off-flags-label">{t("write_off_put_here")}</span>
                                        <input
                                            type="date"
                                            aria-label={t("write_off_put_here_from")}
                                            value={putHere.from}
                                            max={putHere.to || undefined}
                                            onChange={(e) => setPutHere((r) => ({ ...r, from: e.target.value }))}
                                        />
                                        <span aria-hidden="true">–</span>
                                        <input
                                            type="date"
                                            aria-label={t("write_off_put_here_to")}
                                            value={putHere.to}
                                            min={putHere.from || undefined}
                                            onChange={(e) => setPutHere((r) => ({ ...r, to: e.target.value }))}
                                        />
                                        {putHereOn && (
                                            <span className="write-off-flag-count">
                                                {t("write_off_put_here_match", { rows: String(visibleLines.length), units: String(putHereUnits) })}
                                            </span>
                                        )}
                                    </span>
                                    {filtersOn && (
                                        <button type="button" className="write-off-flag-clear" onClick={clearRowFilters}>
                                            <X size={14} />
                                            {t("write_off_filter_clear")}
                                        </button>
                                    )}
                                </div>
                            )}

                            <div className="write-off-table-wrap">
                                {lines.length === 0 ? (
                                    <div className="write-off-empty">
                                        {mode === "location" ? t("write_off_empty_location") : t("write_off_empty_sku", { warehouse })}
                                        {mode === "sku" && elsewhere.length > 0 && (
                                            <div className="write-off-elsewhere">
                                                {t("write_off_elsewhere", {
                                                    list: elsewhere.map((e) => `${e.warehouse} ${e.on_hand}`).join(", "),
                                                })}
                                            </div>
                                        )}
                                    </div>
                                ) : visibleLines.length === 0 ? (
                                    <div className="write-off-empty">{t("write_off_filter_empty")}</div>
                                ) : (
                                    <table className="write-off-table">
                                        <thead>
                                            <tr>
                                                <th>
                                                    <input
                                                        type="checkbox"
                                                        aria-label={t("write_off_select_all")}
                                                        checked={allChecked}
                                                        onChange={(e) => toggleAll(e.target.checked)}
                                                    />
                                                </th>
                                                <th>{mode === "location" ? t("write_off_product") : t("write_off_location")}</th>
                                                <th title={t("write_off_put_here_hint")}>{t("write_off_put_here")}</th>
                                                <th className="num">{t("write_off_units_here")}</th>
                                                <th className="num">{t("write_off_allocated")}</th>
                                                {mode === "location" && <th className="num">{t("write_off_free_stock")}</th>}
                                                <th className="num">{t("write_off_unit_cost")}</th>
                                                <th className="num">{t("write_off_line_value")}</th>
                                                <th className="num">{t("write_off_qty")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {visibleLines.map((l) => {
                                                const key = lineKey(l);
                                                // Row label for aria: the SKU by location, the location name by SKU.
                                                const label = mode === "location" ? l.sku : (l.location_name ?? key);
                                                const entry = selection[key] ?? { checked: false, qty: String(l.writable_qty) };
                                                const qty = parseQty(entry.qty);
                                                const problems = entry.checked ? lineProblems(l, qty, { checkFreeStock: mode === "location" }) : [];
                                                const worst = problems[0];
                                                const blocking = worst ? isBlocking(worst, reason || null) : false;
                                                return (
                                                    <tr
                                                        key={key}
                                                        className={`${entry.checked ? "write-off-row--selected" : ""}${worst ? " write-off-row--problem" : ""}`}
                                                    >
                                                        <td>
                                                            <input
                                                                type="checkbox"
                                                                aria-label={`${t("write_off_select")} ${label}`}
                                                                checked={entry.checked}
                                                                disabled={l.writable_qty === 0}
                                                                onChange={(e) => toggle(key, e.target.checked)}
                                                            />
                                                        </td>
                                                        <td>
                                                            {mode === "location" ? (
                                                                <div className="write-off-product">
                                                                    <span className="write-off-product-title" title={l.title}>{l.title}</span>
                                                                    <span className="write-off-product-sku">{l.sku}{l.barcode ? ` · ${l.barcode}` : ""}</span>
                                                                </div>
                                                            ) : (
                                                                <div className="write-off-location-cell">
                                                                    <MapPin size={14} />
                                                                    <strong>{l.location_name}</strong>
                                                                    <span className={`write-off-sellable write-off-sellable--sm${l.sellable === "unsellable" ? " write-off-sellable--unsellable" : ""}`}>
                                                                        {l.sellable === "unsellable" ? t("write_off_unsellable") : t("write_off_sellable")}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="write-off-put-here">
                                                            {putHereLabel(l) ? (
                                                                <span title={putHereBreakdown(l)}>{putHereLabel(l)}</span>
                                                            ) : (
                                                                <span className="write-off-muted">—</span>
                                                            )}
                                                            {putHereOn && (
                                                                <small>{t("write_off_put_here_in_range", { units: String(unitsPutHereBetween(l, putHere)) })}</small>
                                                            )}
                                                        </td>
                                                        <td className="num">{l.units_here}</td>
                                                        <td className="num">{l.allocated_qty}</td>
                                                        {mode === "location" && <td className="num">{l.free_stock}</td>}
                                                        <td className="num">{formatMoney(l.unit_cost)}</td>
                                                        <td className="num">{formatMoney(l.line_cost)}</td>
                                                        <td className="num">
                                                            <input
                                                                className="write-off-qty"
                                                                aria-label={`${t("write_off_qty")} ${label}`}
                                                                inputMode="numeric"
                                                                value={entry.qty}
                                                                disabled={!entry.checked}
                                                                onChange={(e) => setQty(key, e.target.value)}
                                                            />
                                                            {worst && (
                                                                <span className={`write-off-problem${blocking ? " write-off-problem--block" : ""}`}>
                                                                    {problemText(worst, l)}
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            {lines.length > 0 && (
                                <div className="write-off-footer">
                                    <select
                                        aria-label={t("write_off_reason")}
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                    >
                                        <option value="">{t("write_off_reason_pick")}</option>
                                        {WRITE_OFF_REASONS.map((r) => (
                                            <option key={r} value={r}>{t(reasonKey(r))}</option>
                                        ))}
                                    </select>
                                    <input
                                        aria-label={t("write_off_reference")}
                                        placeholder={t("write_off_reference_placeholder")}
                                        value={reference}
                                        maxLength={500}
                                        onChange={(e) => setReference(e.target.value)}
                                    />
                                    <div className="write-off-totals">
                                        <span>{live.total_units}<small>{t("write_off_units")}</small></span>
                                        <span>{formatMoney(live.total_cost)}<small>{t("write_off_value")}</small></span>
                                    </div>
                                    <button
                                        className="btn btn-danger"
                                        onClick={openConfirm}
                                        disabled={submitting || live.lines.length === 0 || readOnly}
                                        aria-disabled={readOnly}
                                        title={readOnly ? t("read_only_mode") : undefined}
                                    >
                                        <PackageMinus size={18} />
                                        {t("write_off_action")}
                                    </button>
                                    {mode === "sku" && product && live.over_free_total && (
                                        <span className={`write-off-problem write-off-problem--total${reason === "sold_offline" ? " write-off-problem--block" : ""}`}>
                                            {t("write_off_total_over_free_stock", { total: String(live.total_units), free: String(product.free_stock) })}
                                        </span>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {tab === "history" && (
                <div className="write-off-body">
                    <form className="write-off-filters" onSubmit={applyFilters}>
                        <label className="write-off-filter">
                            {t("write_off_filter_from")}
                            <input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
                        </label>
                        <label className="write-off-filter">
                            {t("write_off_filter_to")}
                            <input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} />
                        </label>
                        <label className="write-off-filter">
                            {t("write_off_location")}
                            <input value={filters.location} onChange={(e) => setFilters({ ...filters, location: e.target.value })} />
                        </label>
                        <label className="write-off-filter">
                            {t("write_off_sku")}
                            <input value={filters.sku} onChange={(e) => setFilters({ ...filters, sku: e.target.value })} />
                        </label>
                        <label className="write-off-filter">
                            {t("write_off_reason")}
                            <select value={filters.reason} onChange={(e) => setFilters({ ...filters, reason: e.target.value })}>
                                <option value="">{t("all")}</option>
                                {WRITE_OFF_REASONS.map((r) => (
                                    <option key={r} value={r}>{t(reasonKey(r))}</option>
                                ))}
                            </select>
                        </label>
                        <div className="write-off-filter-actions">
                            <button type="submit" className="btn btn-primary" disabled={historyLoading}>{t("apply")}</button>
                            <button type="button" className="btn btn-ghost" onClick={clearFilters} disabled={historyLoading}>{t("clear")}</button>
                            <button type="button" className="btn btn-ghost" onClick={exportHistory} disabled={exporting || historyLoading}>
                                <Download size={16} />
                                {exporting ? t("loading") : t("write_off_export")}
                            </button>
                        </div>
                    </form>

                    <div className="write-off-table-wrap">
                        {historyRows.length === 0 ? (
                            <div className="write-off-empty">{historyLoading ? t("loading") : t("write_off_history_empty")}</div>
                        ) : (
                            <table className="write-off-table">
                                <thead>
                                    <tr>
                                        <th>{t("write_off_date")}</th>
                                        <th>{t("write_off_who")}</th>
                                        <th>{t("warehouse")}</th>
                                        <th>{t("write_off_location")}</th>
                                        <th>{t("write_off_product")}</th>
                                        <th className="num">{t("write_off_qty")}</th>
                                        <th>{t("write_off_reason")}</th>
                                        <th>{t("write_off_reference")}</th>
                                        <th className="num">{t("write_off_value")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {historyRows.map((r) => (
                                        <tr key={r.id}>
                                            <td>{formatWhen(r.created_at)}</td>
                                            <td>{r.written_off_by_name ?? ""}</td>
                                            <td>{r.warehouse}</td>
                                            <td>{r.location_name}</td>
                                            <td>
                                                <div className="write-off-product">
                                                    <span className="write-off-product-title" title={r.title}>{r.title}</span>
                                                    <span className="write-off-product-sku">{r.sku}</span>
                                                </div>
                                            </td>
                                            <td className="num">{r.quantity}</td>
                                            <td>{t(reasonKey(r.reason))}</td>
                                            <td>{r.reference ?? ""}</td>
                                            <td className="num">{formatMoney(r.cost_value)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    <div className="write-off-paging">
                        <span>{t("write_off_paging", { from: String(pageStart), to: String(pageEnd), total: String(historyTotal) })}</span>
                        <button className="btn btn-ghost" onClick={() => setHistoryPage((p) => Math.max(0, p - 1))} disabled={historyLoading || historyPage === 0}>
                            {t("write_off_prev")}
                        </button>
                        <button className="btn btn-ghost" onClick={() => setHistoryPage((p) => p + 1)} disabled={historyLoading || pageEnd >= historyTotal}>
                            {t("write_off_next")}
                        </button>
                    </div>
                </div>
            )}

            {/* Confirm */}
            {confirm && hasResult && (
                <div className="modal-overlay" onClick={() => !submitting && setConfirm(null)}>
                    <div
                        className="modal-card stock-check-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-label={t("write_off_confirm_title")}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-card-header">
                            <h2>{t("write_off_confirm_title")}</h2>
                            <button className="modal-close" onClick={() => setConfirm(null)} disabled={submitting}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <p className="stock-check-modal-hint">
                                {mode === "location"
                                    ? t("write_off_confirm_body", { location: location?.display_name ?? "" })
                                    : t("write_off_confirm_body_sku", { sku: product?.sku ?? "", count: String(confirm.lines.length) })}
                            </p>
                            <div className="write-off-confirm-list">
                                {confirm.lines.map((l) => (
                                    <div key={l.key} className="write-off-confirm-row">
                                        <div style={{ minWidth: 0 }}>
                                            {mode === "location" ? (
                                                <>
                                                    <div className="write-off-confirm-title">{l.title}</div>
                                                    <div className="write-off-confirm-sku">{l.sku}</div>
                                                </>
                                            ) : (
                                                <div className="write-off-confirm-title">{l.location_name ?? l.key}</div>
                                            )}
                                        </div>
                                        <span className="write-off-confirm-qty">−{l.quantity}</span>
                                        <span>{formatMoney(l.cost_value)}</span>
                                        {l.warnings.length > 0 && (
                                            <span className="write-off-confirm-warning">
                                                <AlertTriangle size={12} /> {t("write_off_confirm_reserved_warning")}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                            {confirm.over_free_total && (
                                <div className="write-off-confirm-warning write-off-confirm-warning--total">
                                    <AlertTriangle size={12} /> {t("write_off_confirm_reserved_warning")}
                                </div>
                            )}
                            <div className="write-off-confirm-meta">
                                <span>{t("write_off_reason")}: <strong>{t(reasonKey(reason))}</strong></span>
                                {reference.trim() && <span>{t("write_off_reference")}: {reference.trim()}</span>}
                            </div>
                            <div className="write-off-confirm-meta">
                                <span>{t("write_off_units")}: <strong>{confirm.total_units}</strong></span>
                                <span>{t("write_off_value")}: <strong>{formatMoney(confirm.total_cost)}</strong></span>
                            </div>
                            <div className="modal-confirm-buttons">
                                <button className="btn btn-ghost btn-lg" onClick={() => setConfirm(null)} disabled={submitting}>
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-danger btn-lg"
                                    onClick={submit}
                                    disabled={submitting || readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                >
                                    {submitting ? t("submitting") : t("write_off_confirm_action")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WriteOff;
